/**
 * Tool registry for the site's agent.
 *
 * Shape borrowed from DeepSeek Harness's plugin model: a tool is a name, a
 * description the model reads, a JSON Schema for its arguments, and a local
 * executor. Registering a new capability is adding one object to the array —
 * nothing else in the loop changes.
 *
 * Every tool here runs entirely in the browser against data the site already
 * ships. None of them make network calls, so the agent cannot reach anything
 * the visitor could not already read.
 */

import { allArticles, Article } from '../Components/Data/articles';
import { SkillsData } from '../Components/Data/skills';
import { ExperienceData } from '../Components/Data/experience';
import { CoreCompetencies } from '../Components/Data/coreCompetencies';
import { BannerContent } from '../Components/Data/banner';
import { buildChart, DATASETS, Dataset } from './charts';
import { BotVariant } from '../Components/Bots/motionSystem';
import { BROWSER_TOOLS } from './browserTools';

export interface ToolContext {
    /** SPA navigation, so the agent can actually take the visitor somewhere */
    navigate: (to: string) => void;
    /** Set the site colour scheme */
    setMode?: (mode: 'light' | 'dark' | 'system') => void;
}

export interface ToolResult {
    /** One line for the visible trace — what the agent did, in plain words */
    summary: string;
    /** Fed back to the model as JSON */
    data: unknown;
}

export interface Tool {
    name: string;
    description: string;
    parameters: Record<string, unknown>;
    run: (args: Record<string, any>, ctx: ToolContext) => ToolResult | Promise<ToolResult>;
    /** Short label for the UI roster */
    label: string;
    /** Each tool gets its own colour so the roster reads as a team, not a list */
    color: string;
    /** One line the visitor can understand, distinct from the model-facing description */
    blurb: string;
    /** Silhouette variation — distinguishable with colour removed */
    variant: BotVariant;
}

// ── retrieval ───────────────────────────────────────────────────────────────

const STOP = new Set([
    'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'is', 'it', 'for', 'on',
    'with', 'what', 'how', 'why', 'does', 'do', 'did', 'about', 'his', 'he',
]);

function terms(q: string): string[] {
    return q.toLowerCase().split(/[^a-z0-9+#.]+/).filter(t => t.length > 1 && !STOP.has(t));
}

/**
 * Field-weighted term scoring. Deliberately simple and inspectable — the point
 * of this tool is that the agent's retrieval is legible to the reader, not that
 * it is state of the art.
 */
function scoreArticle(a: Article, ts: string[]): number {
    const title = a.title.toLowerCase();
    const summary = a.summary.toLowerCase();
    const body = a.content.toLowerCase();
    let score = 0;
    for (const t of ts) {
        if (title.includes(t)) score += 6;
        if (a.category.toLowerCase().includes(t)) score += 3;
        if (summary.includes(t)) score += 2;
        const hits = body.split(t).length - 1;
        score += Math.min(hits, 8) * 0.4;
    }
    return score;
}

export const TOOLS: Tool[] = [
    {
        name: 'search_articles',
        variant: 'hexagon',
        label: 'search',
        color: '#8be9fd',
        blurb: `ranks all ${allArticles.length} articles by keyword`,
        description:
            "Search Abhijith's written articles by keyword. Use this whenever the question touches a technical topic he might have written about — RAG, transformers, backprop, forecasting, LoRA, Salesforce, on-device AI. Returns matching titles, slugs and summaries.",
        parameters: {
            type: 'object',
            properties: {
                query: { type: 'string', description: 'Keywords to search for' },
                limit: { type: 'integer', description: 'Max results, default 4' },
            },
            required: ['query'],
        },
        run: ({ query, limit }) => {
            const ts = terms(String(query || ''));
            const ranked = allArticles
                .map(a => ({ a, s: scoreArticle(a, ts) }))
                .filter(x => x.s > 0)
                .sort((x, y) => y.s - x.s)
                .slice(0, Math.min(Number(limit) || 4, 8));

            return {
                summary: ranked.length
                    ? `searched articles for "${query}" — ${ranked.length} match${ranked.length > 1 ? 'es' : ''}`
                    : `searched articles for "${query}" — nothing matched`,
                data: ranked.map(({ a, s }) => ({
                    slug: a.slug, title: a.title, category: a.category,
                    date: a.date, summary: a.summary, score: Number(s.toFixed(1)),
                })),
            };
        },
    },
    {
        name: 'read_article',
        variant: 'pill',
        label: 'read',
        color: '#bd93f9',
        blurb: 'opens one article and reads its body',
        description:
            'Read the body of one article by its slug, to answer a detailed question accurately instead of guessing. Call search_articles first to find the slug.',
        parameters: {
            type: 'object',
            properties: {
                slug: { type: 'string', description: 'Article slug from search_articles' },
                max_chars: { type: 'integer', description: 'Default 1800. Keep it small; the context budget is shared.' },
            },
            required: ['slug'],
        },
        run: ({ slug, max_chars }) => {
            const a = allArticles.find(x => x.slug === slug);
            if (!a) {
                return { summary: `no article with slug "${slug}"`, data: { error: 'not found' } };
            }
            // Free-tier Groq allows 8k tokens/minute across the whole turn, and a
            // turn may chain several tools. 1800 chars is ~450 tokens: enough to
            // answer from, small enough that three reads still fit.
            const n = Math.min(Number(max_chars) || 1800, 3500);
            return {
                summary: `read "${a.title}"`,
                data: {
                    slug: a.slug, title: a.title, date: a.date,
                    content: a.content.slice(0, n),
                    truncated: a.content.length > n,
                },
            };
        },
    },
    {
        name: 'get_profile',
        variant: 'squircle',
        label: 'profile',
        color: '#50fa7b',
        blurb: 'skills, work history, competencies',
        description:
            "Get structured facts about Abhijith's background: skills, work experience, core competencies, or his professional summary. Use this for questions about what he has done or what he knows, rather than recalling from memory.",
        parameters: {
            type: 'object',
            properties: {
                section: {
                    type: 'string',
                    enum: ['skills', 'experience', 'competencies', 'summary'],
                    description: 'Which part of the profile to return',
                },
            },
            required: ['section'],
        },
        run: ({ section }) => {
            switch (section) {
                case 'skills':
                    return { summary: 'looked up skills', data: SkillsData };
                case 'experience':
                    return { summary: 'looked up work history', data: ExperienceData };
                case 'competencies':
                    return { summary: 'looked up core competencies', data: CoreCompetencies };
                case 'summary':
                default:
                    return {
                        summary: 'looked up profile summary',
                        data: { summary: BannerContent.professionalSummary },
                    };
            }
        },
    },
    {
        name: 'chart',
        variant: 'triangle',
        label: 'chart',
        color: '#f1fa8c',
        blurb: 'draws a chart of the site\'s own data',
        description:
            'Draw a chart when a question is about counts, distribution or "how many". Available datasets: '
            + Object.entries(DATASETS).map(([k, v]) => `"${k}" (${v})`).join('; ')
            + '. The numbers are computed from the site itself, so do not invent values — just pick the dataset.',
        parameters: {
            type: 'object',
            properties: {
                dataset: {
                    type: 'string',
                    enum: Object.keys(DATASETS),
                    description: 'Which question to visualise',
                },
            },
            required: ['dataset'],
        },
        run: ({ dataset }) => {
            const key = (Object.keys(DATASETS).includes(dataset) ? dataset : 'articles_by_category') as Dataset;
            const built = buildChart(key);
            return {
                summary: `charted ${key.replace(/_/g, ' ')} — ${built.caption}`,
                data: {
                    // The model gets the numbers so it can talk about them; the UI
                    // gets the spec so it can draw them.
                    chart: built.input,
                    title: built.title,
                    caption: built.caption,
                    rows: built.input.data.values,
                },
            };
        },
    },
    {
        name: 'navigate',
        variant: 'teardrop',
        label: 'navigate',
        color: '#ffb86c',
        blurb: 'takes you to a page on this site',
        description:
            "Take the visitor to a page on this site. Use it when showing something is better than describing it. Valid routes: '/' (home), '/articles', '/about', '/labs', '/labs/<labId>', '/playground', '/cinema' (a scroll-driven flight through Abhijith's career), '/fit', '/radar', or '/article/<slug>'.",
        parameters: {
            type: 'object',
            properties: {
                route: { type: 'string', description: 'Route beginning with /' },
                reason: { type: 'string', description: 'Why this page answers the question' },
            },
            required: ['route'],
        },
        run: ({ route, reason }, ctx) => {
            const r = String(route || '');
            const ok = /^\/(articles|about|labs(\/[a-z0-9-]+)?|playground|cinema|fit|radar|article\/[a-z0-9-]+)?$/.test(r);
            if (!ok) {
                return { summary: `refused to navigate to "${r}"`, data: { error: 'unknown route' } };
            }
            ctx.navigate(r);
            return {
                summary: `opened ${r}${reason ? ` — ${reason}` : ''}`,
                data: { navigated: r },
            };
        },
    },

    {
        name: 'set_theme',
        variant: 'squircle',
        label: 'theme',
        color: '#bd93f9',
        blurb: 'switches the site between light, dark, and system theme',
        description:
            'Change the site\'s colour scheme. Use this when the user asks to switch to dark mode, ' +
            'light mode, or wants the theme to follow their system setting. ' +
            'Valid values: "dark", "light", "system".',
        parameters: {
            type: 'object',
            properties: {
                mode: {
                    type: 'string',
                    enum: ['dark', 'light', 'system'],
                    description: '"dark", "light", or "system" (follow OS preference)',
                },
            },
            required: ['mode'],
        },
        run: ({ mode }, ctx) => {
            const valid = ['dark', 'light', 'system'];
            const m = String(mode).toLowerCase();
            if (!valid.includes(m)) {
                return { summary: `invalid theme: ${mode}`, data: { error: `must be one of: ${valid.join(', ')}` } };
            }
            ctx.setMode?.(m as 'light' | 'dark' | 'system');
            return {
                summary: `theme set to ${m}`,
                data: { mode: m },
            };
        },
    },
];

// Spread browser tools into the registry — adding a new capability = one call in browserTools.ts
TOOLS.push(...BROWSER_TOOLS);

/** OpenAI/Groq tool-schema form, sent with each request. */
export const toolSchemas = TOOLS.map(t => ({
    type: 'function' as const,
    function: { name: t.name, description: t.description, parameters: t.parameters },
}));

export const findTool = (name: string): Tool | undefined =>
    TOOLS.find(t => t.name === name);
